import {
  Component,
  ViewEncapsulation,
  Input,
  SimpleChanges,
  OnChanges,
  OnInit,
  HostListener,
} from "@angular/core";
import { Dictionary } from "src/app/store/models/common_types";
import { faChartBar } from "@fortawesome/free-solid-svg-icons";
import { RevisionPageDataService } from "../revision-page-data.service";
import { Store } from "@ngrx/store";
import { AppState, stateGetter } from "src/app/store/state/app.state";
import {
  SetDpParams,
  SetRevisionDateForExport,
} from "src/app/store/actions/global-subheader.actions";
import { AppService } from "src/app/app.service";
import dayjs from "dayjs";
import { environment } from "src/environments/environment";
import { SetSourceMetaGridFilters } from "src/app/store/actions/right-panel-actions";
import { VAIconRegistryService, chartBar } from "@va/core-library/icons";

export interface TreeNodeInterface {
  id?: string;
  key: string | number;
  children: Array<any>;
  name: string;
  expand?: boolean;
  level?: number;
  parent?: any;
  view?: string;
  cut?: string;
  shortname?: string;
  dataLoaded?: boolean;
  isTabRow?: boolean;
  isExpandableRow?: boolean;
  source?: string;
}

@Component({
  selector: "app-revision-grid",
  templateUrl: "./revision-grid.component.html",
  styleUrls: ["./revision-grid.component.scss"],
  host: { class: "revision-grid-component" },
  encapsulation: ViewEncapsulation.None,
})
export class RevisionGridComponent implements OnChanges, OnInit {
  @Input() revisionMeta: any;
  @Input() gridFilters: Dictionary<any>;
  faChartBar = faChartBar;
  mapOfExpandData: { [key: string]: boolean } = {};
  listOfData: Array<any>;
  noOfCols: number;
  loading = true;
  gridHeader: Dictionary<any> = {};
  listOfMapData = [];
  periodFireCount = 0;
  paramFireCount = 0;
  gridScrollValue: Dictionary<any> = { x: "0px", y: "0px" };
  showGrid = false;
  defaultRevisionExportDate = "PreQ";
  highlightBackground = "";
  startDateToSend = "PreQ";
  tabLineCountMapping = {};
  selectedSources = [];
  selectedSourceList = [];
  isSourceExcluded = {};

  /* Chart variables */
  chartPopupVisible = false;
  chartPopupType = "";
  companyFullName = "";
  chartPopupDataToUse: any = {};
  beginningStateReferrer = "Revision Analysis Page";
  feature = "REVISION_PAGE";
  /* Chart variables */
  lineItemHeadersExpanded = true;
  assets = environment.assetUrl;
  mapOfExpandedData: { [key: string]: TreeNodeInterface[] } = {};
  popOverMapping: any = {};

  pendingTabsLoaded = true;

  constructor(
    private dataService: RevisionPageDataService,
    private store: Store<AppState>,
    private appService: AppService,
    private iconRegistry: VAIconRegistryService,
  ) {
    this.iconRegistry.registerIcons([chartBar]);
  }

  @HostListener("window:resize", ["$event"])
  onResize(event) {
    this.gridScrollValue.y = `${event.target.innerHeight - 360}px`;
  }

  ngOnInit() {
    this.store
      .select(stateGetter("rightPanel", "sourceMeta", "selectedSources"))
      .subscribe((selectedSources) => {
        this.isSourceExcluded = {};
        this.selectedSources = selectedSources || [];
        this.selectedSourceList = this.selectedSources.map((source) => {
          this.isSourceExcluded[source.name] = source.excluded;
          return source.name;
        });

        const focusedSource = this.selectedSources.find(
          (source) => source.focus,
        );
        const currFocus = focusedSource ? focusedSource.name : "";
        if (this.selectedSourceList.includes(currFocus)) {
          this.selectedSourceList = this.selectedSourceList.filter(
            (sname) => sname !== currFocus,
          );
          this.selectedSourceList.push("Consensus");
        }
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    dayjs.locale("en");
    if (
      changes.revisionMeta &&
      !changes.revisionMeta.firstChange &&
      changes.revisionMeta.currentValue !== changes.revisionMeta.previousValue
    ) {
      this.loading = true;
      this.pendingTabsLoaded = true;
      this.startDateToSend = this.revisionMeta.filters.startDate
        ? this.revisionMeta.filters.startDate
        : "PreQ";
      this.bindRevisionsHeader();
      this.bindRevisionsData();
      this.gridScrollValue = {
        y: `${window.innerHeight - 360}px`,
        x: `${changes.revisionMeta.currentValue.periods.length * 270 + 380}px`,
      };
      this.showGrid = true;
    }

    if (
      changes.gridFilters &&
      !changes.gridFilters.firstChange &&
      changes.gridFilters.currentValue !== changes.gridFilters.previousValue
    ) {
      this.gridFilters = changes.gridFilters.currentValue;
      this.bindRevisionsHeader();
      if (this.gridFilters.showYoyPercent) {
        //show yoy is turned on
        //user history is saved in flow when onFilterChange is called
        this.onChangeYoyPercent();
      } else {
        //show yoy is turned off
        //save user history
        this.saveUserFilters();
      }
    }
  }

  // get data for yoy% calculation
  onChangeYoyPercent() {
    const expandedTabs = this.listOfMapData.filter((tab) => tab.expand).length;

    if (expandedTabs === 0) {
      //page intial load. no tabs are expanded yet
      //this case is when yoy is applied on default load. from pervious user settings
      //this case is handled in bindRevisionsData() => bindLineItems() => createGridRows()
      return;
    }

    this.periodFireCount = 0;
    this.loading = true;
    for (let i = 0; i < this.listOfMapData.length; i++) {
      if (
        this.gridFilters.cut_expansions.indexOf(this.listOfMapData[i].name) !==
        -1
      ) {
        for (let j = 0; j < this.revisionMeta.periods.length; j++) {
          this.getPgData(
            this.listOfMapData[i].shortname,
            this.listOfMapData[i].view,
            this.revisionMeta.periods[j],
            "yoy",
            this.revisionMeta.periods.length * expandedTabs,
          );
        }
      } else {
        this.listOfMapData[i].dataLoaded = false;
        this.listOfMapData.forEach((item) => {
          this.mapOfExpandedData[item.key] = this.convertTreeToList(item);
        });
      }
    }
  }

  bindRevisionsHeader() {
    this.bindGridScroll();

    //header level 1
    this.gridHeader.level1 = [
      {
        label: `All numbers in ${this.revisionMeta.currency}${this.revisionMeta.magnitude} unless stated otherwise`,
        rowspan: 2,
        width: this.lineItemHeadersExpanded ? "300px" : "190px",
        borderRightDotted: true,
        nzLeft: "0px",
        align: "left",
      },
      {
        label: "",
        rowspan: 2,
        width: "30px",
        borderRightDotted: true,
        nzLeft: this.lineItemHeadersExpanded ? "300px" : "190px",
        align: "center",
      },
      {
        label: "",
        rowspan: 2,
        width: "110px",
        borderRight: true,
        nzLeft: this.lineItemHeadersExpanded ? "330px" : "220px",
        align: "left",
      },
    ];

    this.gridHeader.level2 = [];

    for (let i = 0; i < this.revisionMeta.periods.length; i++) {
      this.gridHeader.level1 = this.gridHeader.level1.concat({
        label: this.revisionMeta.periods[i],
        colspan: this.gridFilters.onlyPercentChange
          ? 1
          : this.gridFilters.showYoyPercent
            ? 2
            : 3,
        width: "90px",
        borderRight: true,
        align: "center",
      });
      this.gridHeader.level2 = this.gridHeader.level2.concat(
        {
          label: this.startDateLabel(),
          width: this.gridFilters.onlyPercentChange ? "0px" : "90px",
          borderRightDotted: true,
          valueHeader: true,
          align: "center",
          popoverKey: this.revisionMeta.periods[i] + this.startDateLabel(),
        },
        {
          label: "Current",
          width: this.gridFilters.onlyPercentChange ? "0px" : "90px",
          borderRightDotted: this.gridFilters.showYoyPercent ? false : true,
          valueHeader: true,
          align: "center",
          borderRight: this.gridFilters.showYoyPercent ? true : false,
          popoverKey: `${this.revisionMeta.periods[i]}Current`,
        },
        {
          label: "%Change",
          width: this.gridFilters.showYoyPercent ? "0px" : "90px",
          borderRight: true,
          valueHeader: true,
          align: "center",
          popoverKey: "",
        },
      );
    }

    this.gridHeader = { ...this.gridHeader };
  }

  bindGridScroll() {
    const colWidth = this.gridFilters.onlyPercentChange
      ? 90
      : this.gridFilters.showYoyPercent
        ? 180
        : 270;
    const additional = this.lineItemHeadersExpanded ? 380 : 270;
    if (this.gridScrollValue) {
      const xScrollValue = `${
        this.revisionMeta.periods.length * colWidth + additional
      }px`;
      this.gridScrollValue = {
        ...this.gridScrollValue,
        x: xScrollValue,
      };
    }
  }

  bindRevisionsData() {
    this.listOfMapData = this.revisionMeta.tabs;
    this.bindLineitems();
  }

  bindLineitems() {
    const tabsCount = this.revisionMeta.tabs.filter((tab) =>
      this.revisionMeta.filters.cut_expansions.includes(tab.name),
    ).length;
    this.periodFireCount = 0;
    for (
      let tabIndex = 0;
      tabIndex < this.revisionMeta.tabs.length;
      tabIndex++
    ) {
      if (
        this.revisionMeta.filters.cut_expansions.indexOf(
          this.revisionMeta.tabs[tabIndex].name,
        ) !== -1
      ) {
        this.dataService
          .getLineitemsData(
            this.revisionMeta.tabs[tabIndex].shortname,
            this.revisionMeta.tabs[0].view,
            this.revisionMeta.ticker,
          )
          .subscribe(
            (response) => {
              const lineitemData = response;
              this.createGridRows(
                lineitemData,
                this.revisionMeta.tabs[tabIndex].shortname,
                tabsCount,
              );
            },
            (err) => {
              console.error(err);
            },
          );
        const revisionExportDate =
          Object.keys(this.revisionMeta.filters).length > 0 &&
          this.revisionMeta.filters.startDate !== undefined
            ? this.revisionMeta.filters.startDate
            : this.defaultRevisionExportDate;
        this.store.dispatch(new SetRevisionDateForExport(revisionExportDate));
      }
    }
  }

  createGridRows(lineitemData: any, tab: string, tabsCount = 1) {
    this.listOfMapData = this.listOfMapData.map((el, i) => {
      const data = Object.assign({}, el);
      data.key = i + 1;
      data.isTabRow = true;
      data.expand =
        this.gridFilters.cut_expansions.indexOf(el.name) !== -1 ? true : false;
      if (tab === el.shortname) {
        data.children = lineitemData.row_dimensions[0].values.map((obj, j) => {
          const l1Data = Object.assign({}, obj);
          l1Data["key"] = `l1-${j}${1}`;
          l1Data["source"] = obj.header ? null : this.revisionMeta.focus;
          l1Data["isExpandableRow"] = obj.header ? false : true;
          l1Data["parameter_unit"] =
            obj.parameter_unit !== undefined ? obj.parameter_unit : "";
          if (!obj.header && this.selectedSourceList.length > 0) {
            l1Data["children"] = this.selectedSourceList.map((source, k) => {
              const l2Data = Object.assign({}, {});
              l2Data["key"] = `l1${j}${1}-${k}${1}`;
              l2Data["source"] = source;
              l2Data["childRow"] = true;
              return l2Data;
            });
          }
          return l1Data;
        });

        if (data.expand === true) {
          // get data only if expanded

          /** 
          we want to only start rendering after all below pg calls are finished
          how many are they?? 
          here we are firing pg call
          for every open tab => for every period => 1 or 2 pg calls based on showYoy
          i.e fireCount = (no. open tabs) * (periods) * (2 if showYoy is ON)
          similar logic is used every place where fireCount is being calculated
          */
          let fireCount = this.revisionMeta.periods.length;
          fireCount = fireCount * tabsCount;
          fireCount = fireCount * (this.gridFilters.showYoyPercent ? 2 : 1);
          for (let i = 0; i < this.revisionMeta.periods.length; i++) {
            if (this.gridFilters.showYoyPercent) {
              this.getPgData(
                tab,
                el.view,
                this.revisionMeta.periods[i],
                "yoy",
                fireCount,
              );
            }

            this.getPgData(
              tab,
              el.view,
              this.revisionMeta.periods[i],
              "initial",
              fireCount,
            );
          }
          data.dataLoaded = true;
        }
      }
      return data;
    });
    this.listOfMapData.forEach((item) => {
      this.mapOfExpandedData[item.key] = this.convertTreeToList(item);
    });
  }

  getPgData(
    tab: string,
    cut: string,
    period: string,
    load: string,
    periodsCount: number,
  ) {
    const growthType = load === "yoy" ? "yoy" : "Analytics";
    this.dataService
      .getPgData(
        period,
        tab,
        cut,
        this.revisionMeta.ticker,
        this.revisionMeta.focus,
        growthType,
        this.startDateToSend,
        this.startDateToSend,
      )
      .subscribe((response) => {
        this.periodFireCount++;

        if (response.data.length === 0) {
          //handling edge case when response.data is an empty list
          response.data = [{ data: [] }, { data: [] }];
        }
        const currentData = response.data[0].data;
        const preQData = response.data[1] ? response.data[1].data : [];

        // const pg = response.data[0].PG;
        const period = response.data[0].period;
        const currentRevision = response.data[0].revision;
        const preQRevision = this.startDateLabel();
        const currentRevDate = response.data[0].revision_date;
        const preQRevDate = response.data[1]
          ? response.data[1].revision_date
          : "";
        this.popOverMapping[`${period}${currentRevision}`] = currentRevDate;
        this.popOverMapping[`${period}${preQRevision}`] = preQRevDate;

        this.listOfMapData
          .filter((el) => {
            return el.shortname === tab;
          })[0]
          .children.map((data) => {
            if (load !== "yoy") {
              // set default data
              data[`current-${period}`] = null;
              data[`percent-change-${period}`] = null;
              data[`current-dpid-${period}`] = null;
              data[`unit-${period}`] = null;
              data[`revision-${period}`] = null;
              data[`revision-dpid-${period}`] = null;
              data["precision"] = null;

              const currentVal = currentData.filter(function (value) {
                return value.param_id.toString() === data.id.toString();
              })[0];
              const preqVal = preQData.filter(function (value) {
                return value.param_id.toString() === data.id.toString();
              })[0];
              data.source = data.header ? "" : this.revisionMeta.focus;
              if (currentVal) {
                data[`current-${period}`] = currentVal.value;
                data[`percent-change-${period}`] = currentVal.percentage_change;
                data[`current-dpid-${period}`] = currentVal.id;
                data[`unit-${period}`] = currentVal.unit;
                data["precision"] = currentVal.precision;
              }
              if (preqVal) {
                data[`revision-${period}`] = preqVal.value;
                data[`revision-dpid-${period}`] = preqVal.id;
              }
            } else if (load === "yoy") {
              const currentVal = currentData.filter(function (value) {
                return value.param_id.toString() === data.id.toString();
              })[0];
              const preqVal = preQData.filter(function (value) {
                return value.param_id.toString() === data.id.toString();
              })[0];
              data[`current-yoy-${period}`] = currentVal
                ? currentVal.value
                : null;
              data[`current-yoy-dpid-${period}`] = currentVal
                ? currentVal.id
                : null;
              data[`revision-yoy-${period}`] = preqVal ? preqVal.value : null;
              data[`revision-yoy-dpid-${period}`] = preqVal ? preqVal.id : null;
            }
            // resetting expansions
            data.expand = false;
            data.dataLoaded = false;
            return data;
          });

        if (this.periodFireCount === periodsCount) {
          // after all pg calls done
          setTimeout(() => {
            this.listOfMapData.forEach((item) => {
              this.mapOfExpandedData[item.key] = this.convertTreeToList(item);
            });
            this.loading = false;

            if (this.pendingTabsLoaded) {
              this.pendingTabsLoaded = false;
              this.bindPendingTabLines();
            }
            this.onFilterChange();
          }, 100);
        }
      });
  }

  getParamData(
    cut: string,
    tab: string,
    id: string,
    period: string,
    totalPeriods: number,
    from?: string,
    growthType?: string,
  ) {
    const params = {
      ticker: this.revisionMeta.ticker,
      cut: cut,
      pg: tab,
      paramId: parseInt(id),
      period: period,
      sources: this.selectedSourceList,
      revision: this.startDateToSend,
      growthType: growthType,
    };

    //should send -xMonth|MM-DD-YYYY instead of only -xMonth previously
    //ref bug: CACHE-3206
    if (["-1Month", "-3Month"].includes(this.startDateToSend)) {
      const months = this.startDateToSend === "-1Month" ? 1 : 3;
      const dateStr = dayjs().subtract(months, "month").format("MM-DD-YYYY");
      params.revision = `${this.startDateToSend}|${dateStr}`;
    }

    this.dataService.getParamData(params).subscribe((response) => {
      this.paramFireCount++;
      if (response.data && response.data.length > 0) {
        this.listOfMapData
          .filter((el) => {
            return el.shortname === tab;
          })[0]
          .children.filter((data) => {
            return data.id === id;
          })
          .map((rowData) => {
            rowData.children.map((sourceData) => {
              const revisionData = response.data.filter((data) => {
                return (
                  data.key.split("|")[0].toLowerCase() ===
                    sourceData.source.toLowerCase() &&
                  data.revision ===
                    (this.startDateToSend.indexOf("|") > -1
                      ? this.startDateToSend.split("|")[0]
                      : this.startDateToSend)
                );
              })[0];
              const currentData = response.data.filter((data) => {
                return (
                  data.key.split("|")[0].toLowerCase() ===
                    sourceData.source.toLowerCase() &&
                  data.revision.toLowerCase() === "current"
                );
              })[0];
              if (growthType !== "yoy") {
                if (currentData && currentData.data.length > 0) {
                  sourceData[`current-${currentData.data[0].period}`] =
                    currentData.data[0].value;
                  sourceData[`percent-change-${currentData.data[0].period}`] =
                    currentData.data[0].percentage_change;
                  sourceData[`current-dpid-${currentData.data[0].period}`] =
                    currentData.data[0].id;
                  sourceData[`unit-${currentData.data[0].period}`] =
                    currentData.data[0].unit;
                  sourceData["precision"] = currentData.data[0].precision;
                  sourceData["dapparameterid"] =
                    `${currentData.data[0].param_id}` || null;
                } else {
                  // set default data
                  sourceData[`current-${params.period}`] = null;
                  sourceData[`percent-change-${params.period}`] = null;
                  sourceData[`current-dpid-${params.period}`] = null;
                  sourceData[`unit-${params.period}`] = null;
                  sourceData["precision"] = null;
                  sourceData["dapparameterid"] = null;
                }
              } else if (growthType === "yoy") {
                sourceData[`current-yoy-${params.period}`] =
                  currentData && currentData.data.length > 0
                    ? currentData.data[0].value
                    : null;
                sourceData[`current-yoy-dpid-${params.period}`] =
                  currentData && currentData.data.length > 0
                    ? currentData.data[0].id
                    : null;
              }

              if (growthType !== "yoy") {
                if (revisionData && revisionData.data.length > 0) {
                  sourceData[`revision-${revisionData.data[0].period}`] =
                    revisionData.data[0].value;
                  sourceData[`revision-dpid-${revisionData.data[0].period}`] =
                    revisionData.data[0].id;
                  sourceData["dapparameterid"] =
                    `${revisionData.data[0].param_id}` || null;
                } else {
                  sourceData[`revision-${params.period}`] = null;
                  sourceData[`revision-dpid-${params.period}`] = null;
                  sourceData["dapparameterid"] = null;
                }
              } else if (growthType === "yoy") {
                sourceData[`revision-yoy-${params.period}`] =
                  revisionData && revisionData.data.length > 0
                    ? revisionData.data[0].value
                    : null;
                sourceData[`revision-yoy-dpid-${params.period}`] =
                  revisionData && revisionData.data.length > 0
                    ? revisionData.data[0].id
                    : null;
              }
              return sourceData;
            });
            rowData.expand = true;
            return rowData;
          });

        if (this.paramFireCount === totalPeriods) {
          setTimeout(() => {
            this.loading = false;
            this.paramFireCount = 0;
            this.listOfMapData.forEach((item) => {
              this.mapOfExpandedData[item.key] = this.convertTreeToList(item);
            });
          }, 50);
        }
      }
    });
  }

  bindPendingTabLines() {
    const tabsCount = this.revisionMeta.tabs.filter(
      (tab) => !this.revisionMeta.filters.cut_expansions.includes(tab.name),
    ).length;
    this.periodFireCount = 0;
    for (
      let tabIndex = 0;
      tabIndex < this.revisionMeta.tabs.length;
      tabIndex++
    ) {
      if (
        this.revisionMeta.filters.cut_expansions.indexOf(
          this.revisionMeta.tabs[tabIndex].name,
        ) === -1
      ) {
        this.dataService
          .getLineitemsData(
            this.revisionMeta.tabs[tabIndex].shortname,
            this.revisionMeta.tabs[0].view,
            this.revisionMeta.ticker,
          )
          .subscribe((response) => {
            const lineitemData = response;
            this.createGridRows(
              lineitemData,
              this.revisionMeta.tabs[tabIndex].shortname,
              tabsCount,
            );
          });
      }
    }
  }

  collapse(
    array: TreeNodeInterface[],
    data: TreeNodeInterface,
    $event: boolean,
    eventIsSynthetic?: boolean,
  ): void {
    if (!eventIsSynthetic && (data.isTabRow || data.isExpandableRow)) {
      const extras = { beginning_state_referrer: "Revision Analysis Page" };
      let actionSource = "";
      const mainSource = "";
      let expansionCut = "";
      if (data.isTabRow) {
        //Shows that it is a tab expansion
        if (data.expand === true) {
          actionSource = "Revision Tab Line Expand";
        } else {
          actionSource = "Revision Tab Line Collapse";
        }
        extras["tab"] = data.name;
        expansionCut = data.view;
      } else {
        //Shows that it is a source expansion within a tab
        if (data.expand === true) {
          actionSource = "Revision Source Line Expand";
        } else {
          actionSource = "Revision Source Line Collapse";
        }
        extras["source"] = this.revisionMeta.focus;
        extras["source_list"] = this.selectedSourceList;
        expansionCut = data.parent.view;
      }

      this.appService.logUserAction(
        {},
        this.revisionMeta.ticker,
        expansionCut,
        actionSource,
        extras,
        mainSource,
        actionSource,
      );
    }

    if (data.isTabRow) {
      if (
        data.expand &&
        this.gridFilters.cut_expansions.indexOf(data.name) === -1
      ) {
        //add to cut expansions
        this.gridFilters.cut_expansions.push(data.name);
      } else {
        //remove from cut expansions
        this.gridFilters.cut_expansions =
          this.gridFilters.cut_expansions.filter(
            (cutName) => cutName !== data.name,
          );
      }
      this.saveUserFilters();
    }

    if (data.isTabRow) {
      this.listOfMapData
        .filter((listdata) => {
          return listdata.shortname === data.shortname;
        })
        .map((obj) => {
          obj.expand = data.expand;
          if (data.expand === true) {
            obj.dataLoaded = true;
          }
          return obj;
        });
    } else if (data.isExpandableRow) {
      this.listOfMapData
        .filter((listdata) => {
          return listdata.shortname === data.parent.shortname;
        })[0]
        .children.map((obj) => {
          if (data.id === obj.id) {
            obj.expand = data.expand;
            if (obj.expand) {
              obj.dataLoaded = true;
            }
          }
          return obj;
        });
    }

    /* 
    $event = true ---> Expanding the tab row 
    $event = false ---> Collapsing the tab row
    */

    if ($event === false) {
      if (data.children) {
        data.children.forEach((d) => {
          const target = array.find((a) => a.key === d.key)!;
          target.expand = false;
          this.collapse(array, target, false, true);
        });
      } else {
        return;
      }
    } else {
      if (!data.dataLoaded && data.expand === true && data.isTabRow) {
        this.loading = true;
        this.periodFireCount = 0;
        for (let i = 0; i < this.revisionMeta.periods.length; i++) {
          let fireCount = this.revisionMeta.periods.length;
          fireCount = fireCount * (this.gridFilters.showYoyPercent ? 2 : 1);
          if (this.gridFilters.showYoyPercent) {
            this.getPgData(
              data.shortname,
              data.view,
              this.revisionMeta.periods[i],
              "yoy",
              fireCount,
            );
          }

          this.getPgData(
            data.shortname,
            data.view,
            this.revisionMeta.periods[i],
            "click",
            fireCount,
          );
        }
      } else if (data.dataLoaded && this.gridFilters.showYoyPercent === true) {
        this.onChangeYoyPercent();
      }

      if (!data.dataLoaded && data.isExpandableRow && data.expand === true) {
        this.paramFireCount = 0;
        this.loading = true;
        for (let i = 0; i < this.revisionMeta.periods.length; i++) {
          this.getParamData(
            data.parent.view,
            data.parent.shortname,
            data.id,
            this.revisionMeta.periods[i],
            this.revisionMeta.periods.length,
            "",
            "Analytics",
          );

          if (this.gridFilters.showYoyPercent) {
            this.getParamData(
              data.parent.view,
              data.parent.shortname,
              data.id,
              this.revisionMeta.periods[i],
              this.revisionMeta.periods.length,
              "",
              "yoy",
            );
          }
        }
      }
    }
  }

  convertTreeToList(root: TreeNodeInterface): TreeNodeInterface[] {
    const stack: TreeNodeInterface[] = [];
    const array: TreeNodeInterface[] = [];
    const hashMap = {};
    stack.push({
      ...root,
      level: 0,
      expand: root.expand ? root.expand : false,
    });

    // if filter is already applied show count of all visible lines
    if (
      Object.keys(this.gridFilters).length > 0 &&
      this.gridFilters.filterLineItem !== undefined &&
      this.gridFilters.filterLineItem.trim().length > 0
    ) {
      this.tabLineCountMapping[root.name] = root.children
        ? root.children.filter(
            (child) => child.hidden === false && child.header === undefined,
          ).length
        : "";
    } else {
      // remove dummy header from count
      this.tabLineCountMapping[root.name] = root.children
        ? root.children.filter((child) => child.header === undefined).length
        : "";
    }

    while (stack.length !== 0) {
      const node = stack.pop()!;
      this.visitNode(node, hashMap, array);
      if (node.children) {
        for (let i = node.children.length - 1; i >= 0; i--) {
          stack.push({
            ...node.children[i],
            level: node.level! + 1,
            expand: node.children[i].expand ? true : false,
            parent: node,
          });
        }
      }
    }

    return array;
  }

  visitNode(
    node: TreeNodeInterface,
    hashMap: { [key: string]: boolean },
    array: TreeNodeInterface[],
  ): void {
    if (!hashMap[node.key]) {
      hashMap[node.key] = true;
      array.push(node);
    }
  }

  getExpandedColumnHeight(item) {
    return item.isExpandableRow && item.expand
      ? this.selectedSourceList.length * 31
      : 31;
  }

  onFilterChange(param?: Array<string>) {
    this.listOfMapData.map((data) => {
      if (data.children) {
        data.children.map((rowData) => {
          rowData.hidden = false;
          // percent change filter start
          rowData["highlight"] = [];
          if (this.gridFilters.percentChange === "default") {
            rowData.hidden = false;
            for (let i = 0; i < this.revisionMeta.periods.length; i++) {
              rowData[`highlight-green-${this.revisionMeta.periods[i]}`] =
                false;
              rowData[`highlight-red-${this.revisionMeta.periods[i]}`] = false;
            }
          } else {
            if (rowData.header) {
              rowData.hidden = true;
            } else {
              let filterPass = 0;
              for (let i = 0; i < this.revisionMeta.periods.length; i++) {
                let val = Number(this.gridFilters.percentChange);
                val = rowData.unit === "%" ? val * 100 : val;
                let currentVal =
                  rowData[`percent-change-${this.revisionMeta.periods[i]}`];
                if (currentVal !== undefined && currentVal !== null) {
                  currentVal =
                    typeof currentVal == "string"
                      ? currentVal.replace(",", "")
                      : currentVal;
                  currentVal = Math.abs(currentVal);
                  if (
                    rowData[`percent-change-${this.revisionMeta.periods[i]}`] >=
                    val
                  ) {
                    rowData[`highlight-green-${this.revisionMeta.periods[i]}`] =
                      true;
                    rowData[`highlight-red-${this.revisionMeta.periods[i]}`] =
                      false;
                  } else if (
                    rowData[`percent-change-${this.revisionMeta.periods[i]}`] <
                    -val
                  ) {
                    rowData[`highlight-red-${this.revisionMeta.periods[i]}`] =
                      true;
                    rowData[`highlight-green-${this.revisionMeta.periods[i]}`] =
                      false;
                  } else {
                    rowData[`highlight-green-${this.revisionMeta.periods[i]}`] =
                      false;
                    rowData[`highlight-red-${this.revisionMeta.periods[i]}`] =
                      false;
                  }

                  if (currentVal >= val) {
                    filterPass++;
                  }
                }
              }

              if (filterPass > 0) {
                rowData.hidden = false;
              } else {
                rowData.hidden = true;
              }
            }
          }
          // percent change filter end

          // lineitem filter code start
          if (rowData.header) {
            rowData.hidden = true;
          }
          if (!rowData.hidden) {
            if (
              rowData.name
                .toLowerCase()
                .indexOf(this.gridFilters.filterLineItem.toLowerCase()) !== -1
            ) {
              rowData.hidden = false;
            } else {
              rowData.hidden = true;
            }
          }
          // lineitem filter code end
          rowData.expand = false;
          return rowData;
        });
      }
      return data;
    });
    this.listOfMapData.forEach((item) => {
      this.mapOfExpandedData[item.key] = this.convertTreeToList(item);
    });

    //save user preference
    this.saveUserFilters();

    //Logging
    if (param) {
      if (param.indexOf("percentChange") !== -1) {
        const selected =
          this.gridFilters.percentChange === "default"
            ? "All"
            : `${this.gridFilters.percentChange}%`;
        const percentChangeExtras = {
          beginning_state_referrer: "Revision Analysis Page",
          "Filter type": selected,
        };
        this.appService.logUserAction(
          {},
          this.revisionMeta.ticker,
          "",
          "Revision Percent Filter",
          percentChangeExtras,
          "",
          "Revision Percent Filter Click",
        );
      }
      if (
        param.indexOf("lineitem") !== -1 &&
        this.gridFilters.filterLineItem !== ""
      ) {
        const liTextChangeExtras = {
          beginning_state_referrer: "Revision Analysis Page",
          term_entered: this.gridFilters.filterLineItem,
        };
        this.appService.logUserAction(
          {},
          this.revisionMeta.ticker,
          "",
          "Revision line item filter",
          liTextChangeExtras,
          "",
          "Revision Line item filter",
        );
      }
    }

    this.setClearStatusInRightPanel();
  }

  setDPParams(item, period, columnName) {
    this.highlightBackground = "";
    if (columnName !== "percent-change") {
      const ticker = this.revisionMeta.ticker;
      const cut =
        item.parent.view !== undefined
          ? item.parent.view
          : item.childRow !== undefined && item.childRow
            ? item.parent.parent.view
            : "";
      const source = item.source;
      const revision = columnName.includes("revision")
        ? this.startDateLabel()
        : "Current";
      const date = new Date(this.popOverMapping[period + revision]);

      const dpId = item[`${columnName}-dpid-${period}`].toString();

      /**
       * dpId and dpIdForExtras remain same for data flowing through pg calls
       * For revision data flowing through param3 calls dpId received for a data point is
       * of format id|{{current_date}}
       * instead of id|{{revision_date}}
       * So dpIdForExtras is essentially building the expected id|{{revision_date}}
       * ref bug: CACHE-2468
       */
      const dpIdForExtras = `${dpId.split("|")[0]}|${dayjs(date).format(
        "MM/DD/YYYY",
      )}`;

      this.highlightBackground = dpId + columnName;
      const growthType = columnName.includes("yoy") ? "YoY%" : "Analytics";

      const extrasRevParam =
        revision !== "Current" ? this.getDateDiff() : "current";
      const extras = `${
        dpId.split("|")[0]
      }|${period.toLowerCase()}${extrasRevParam}${source}`;
      const dpParams = {
        dp: dpIdForExtras,
        cut: cut,
        ticker: ticker,
        source: source,
        growthType: growthType,
        shouldLog: true,
        historical_focus: "VA Actuals",
        period: period,
        param: item.dapparameterid,
        extras: extras,
        is_revision_page: true,
      };

      if (revision.toLowerCase() === "preq") {
        dpParams["revision"] = "PreQ|PreQ";
      } else {
        dpParams["revision"] = extrasRevParam;
      }

      this.appService.logUserAction(
        {},
        ticker,
        cut,
        "Revision cell click",
        {
          beginning_state_referrer: "Revision Analysis Page",
          period: period,
          dpid: dpId,
        },
        source,
        "Revision cell click",
      );

      this.store.dispatch(new SetDpParams(dpParams));
    }
  }

  getDateDiff = () => {
    if (this.startDateToSend === "-1Month") {
      return "-1m";
    }

    if (this.startDateToSend === "-3Month") {
      return "-3m";
    }

    if (this.startDateToSend.toLocaleLowerCase() === "preq") {
      return "preq";
    }

    return this.appService
      .getFormattedDateDifference(
        dayjs(this.startDateToSend.split("|").pop()).toDate(),
      )
      .toLocaleLowerCase();
  };

  //Trigger the popup modal for charts
  showChartPopup(rowData: any, chartType: string) {
    this.chartPopupDataToUse = {
      companyFullName: this.revisionMeta.tickerFullName,
      period: this.revisionMeta.periods[0],
      source: rowData.source,
      // sourceList: [rowData.source, "Consensus"],
      sourceList: ["Consensus"]
        .concat(this.selectedSourceList)
        .map((source) => {
          return {
            label: source,
            value: source,
            checked: source === rowData.source || source === "Consensus",
          };
        }),
      cut: "NMV",
      forecast: true,
      gAdjust: "implied",
      export: true,
      dapparamId: rowData.childRow
        ? rowData.parent.dapparameterid
        : rowData.dapparameterid,
      ticker: this.revisionMeta.ticker,
    };
    this.chartPopupType = chartType;
    this.chartPopupVisible = true;
  }
  //Close the popup modal for charts
  hideChartPopup() {
    this.chartPopupVisible = false;
    this.chartPopupType = "";
  }
  startDateChange() {
    if (this.gridFilters.startDate !== "customDate") {
      this.startDateToSend = this.gridFilters.startDate;
    }

    if (
      this.startDateToSend === this.gridFilters.startDate ||
      this.startDateToSend.indexOf("|") > -1
    ) {
      const labelText = this.startDateLabel();

      for (let i = 0; i < this.gridHeader.level2.length; i++) {
        const columnLength = 3; // update with integration of YOY%
        if (i % columnLength === 0) {
          this.gridHeader.level2[i].label = labelText;
          for (let j = 0; j < this.revisionMeta.periods.length; j++) {
            this.gridHeader.level2[i].popoverKey =
              `${this.revisionMeta.periods[j]}${labelText}`;
          }
          this.gridHeader = { ...this.gridHeader };
        }
      }

      this.loading = true;
      this.periodFireCount = 0;
      const expandedTabsCount = this.listOfMapData.filter(
        (tab) => tab.expand,
      ).length;
      for (let i = 0; i < this.listOfMapData.length; i++) {
        if (this.listOfMapData[i].expand === true) {
          let fireCount = this.revisionMeta.periods.length;
          fireCount = fireCount * expandedTabsCount;
          fireCount = fireCount * (this.gridFilters.showYoyPercent ? 2 : 1);
          for (let j = 0; j < this.revisionMeta.periods.length; j++) {
            this.getPgData(
              this.listOfMapData[i].shortname,
              this.listOfMapData[i].view,
              this.revisionMeta.periods[j],
              "click",
              fireCount,
            );

            if (this.gridFilters.showYoyPercent) {
              this.getPgData(
                this.listOfMapData[i].shortname,
                this.listOfMapData[i].view,
                this.revisionMeta.periods[j],
                "yoy",
                fireCount,
              );
            }
          }
        } else {
          this.listOfMapData[i].dataLoaded = false;
          this.listOfMapData.forEach((item) => {
            this.mapOfExpandedData[item.key] = this.convertTreeToList(item);
          });
        }
      }

      const extras = { beginning_state_referrer: "Revision Analysis Page" };
      let selected = "";
      switch (this.gridFilters.startDate) {
        case "customDate":
          selected = "Custom";
          extras["date_selected"] = this.startDateLabel();
          break;
        case "-1Month":
          selected = "1M";
          break;
        case "-3Month":
          selected = "3M";
          break;
        case "PreQ":
          selected = "PreQ";
          break;
        default:
          selected = "PreQ";
      }
      this.appService.logUserAction(
        {},
        this.revisionMeta.ticker,
        null,
        `Revision Date ${selected}`,
        extras,
        null,
        "Revision Date Select",
      );
      this.setClearStatusInRightPanel();
    }
  }

  startDateLabel() {
    let labelText = "";
    switch (this.startDateToSend) {
      case "-1Month": {
        labelText = "1 Month";
        break;
      }
      case "-3Month": {
        labelText = "3 Months";
        break;
      }
      case "PreQ": {
        labelText = "PreQ";
        break;
      }
      default: {
        const date = this.startDateToSend.split("|").pop();
        labelText = dayjs(new Date(date)).format("DD-MMM-YY");
      }
    }
    return labelText;
  }

  onChangeCustomDate(event) {
    if (event !== null) {
      // handling for clear date
      const diffVal = this.appService.getFormattedDateDifference(event);
      this.startDateToSend = `${diffVal}|${dayjs(event).format("MM-DD-YYYY")}`;
      this.startDateChange();
    }
  }

  onShowMorePeriodsClicked() {
    this.store.dispatch(new SetSourceMetaGridFilters(true));
    const lastPeriod =
      this.revisionMeta.periods[this.revisionMeta.periods.length - 1];
    const lastPeriodYear = Number(lastPeriod.split("-")[1]);
    const periodsToAdd = [
      `FY-${lastPeriodYear + 1}`,
      `FY-${lastPeriodYear + 2}`,
      `FY-${lastPeriodYear + 3}`,
    ];
    const totalRemaining = this.revisionMeta.end_fiscal - lastPeriodYear;
    this.addPeriod(periodsToAdd.slice(0, Math.min(totalRemaining, 3)));
  }

  addPeriod(periods) {
    //add periods to revision meta
    this.revisionMeta.periods = [...this.revisionMeta.periods, ...periods];
    this.gridFilters.period_list = [...this.revisionMeta.periods];
    this.gridFilters.show_clear = true;
    // const allPeriods = this.revisionMeta.periods;

    const colWidth = "90px";
    const colSpan = this.gridFilters.onlyPercentChange ? 1 : 3;

    //add column to the level1 headers
    const newHeadersL1 = [];
    periods.forEach((period) => {
      newHeadersL1.push({
        label: period,
        colspan: colSpan,
        width: colWidth,
        borderRight: true,
      });
    });

    this.gridHeader.level1 = [...this.gridHeader.level1, ...newHeadersL1];
    this.gridHeader = { ...this.gridHeader };

    //adjust the grid width for newly added columns
    this.bindRevisionsHeader();

    //identify expanded tabs
    const expandedTabs = this.listOfMapData.filter((tab) => tab.expand);

    this.periodFireCount = 0;
    expandedTabs.forEach((tab) => {
      periods.forEach((period) => {
        this.loading = true;
        let fireCount = periods.length;
        fireCount = fireCount * expandedTabs.length;
        fireCount = fireCount * (this.gridFilters.showYoyPercent ? 2 : 1);
        if (this.gridFilters.showYoyPercent) {
          this.getPgData(tab.shortname, tab.view, period, "yoy", fireCount);
        }

        this.getPgData(tab.shortname, tab.view, period, "showmore", fireCount);
      });
    });
  }

  onLineItemColumnCollapse() {
    this.lineItemHeadersExpanded = !this.lineItemHeadersExpanded;
    this.bindRevisionsHeader();
  }

  saveUserFilters() {
    const jsonData = {
      filters: { ...this.gridFilters, startDate: this.startDateToSend },
    };
    const ticker = this.revisionMeta.ticker;
    this.dataService.saveUserFilters(jsonData, ticker).subscribe();
  }

  setClearStatusInRightPanel() {
    // same object returns true, not same then false
    // true -> Don't set clear Flag
    // false -> set clear Flag
    const defaultGridFilters = {
      percentChange: "default",
      startDate: "PreQ",
      filterLineItem: "",
      cut_expansions: ["Monitor"],
      showYoyPercent: false,
      onlyPercentChange: false,
      period_list: this.revisionMeta.periods,
    };
    const diffInFilter = this.appService.compare(
      defaultGridFilters,
      this.gridFilters,
    );
    this.store.dispatch(new SetSourceMetaGridFilters(!diffInFilter));
  }
}
